import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, X, Sparkles } from 'lucide-react';
import { EMOJIS } from '../constants/emojis';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'items' | 'members' | 'scans' | 'analytics';  // âœ… Added 'scans'
  currentCount?: number;
  maxCount?: number;
}

export function UpgradePrompt({ isOpen, onClose, limitType, currentCount, maxCount }: UpgradePromptProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getContent = () => {
    switch (limitType) {
      case 'items':
        return {
          title: 'Item Limit Reached',
          description: `You've reached your limit of ${maxCount} active items. Upgrade to Premium for unlimited items!`,
          icon: EMOJIS.FRIDGE,
        };
      case 'members':
        return {
          title: 'Member Limit Reached',
          description: `You've reached your limit of ${maxCount} household members. Upgrade to Premium for unlimited members!`,
          icon: EMOJIS.PEOPLE,
        };
      case 'scans':  // âœ… NEW: Barcode scan limit
        return {
          title: 'Barcode Scan Limit Reached',
          description: `You've used all ${maxCount} barcode scans this month. Upgrade to Premium for unlimited scanning!`,
          icon: EMOJIS.CAMERA,
        };
      case 'analytics':
        return {
          title: 'Premium Feature',
          description: 'Advanced analytics are available with Premium. Upgrade to unlock detailed insights!',
          icon: EMOJIS.CHART,
        };
      default:
        return {
          title: 'Upgrade to Premium',
          description: 'Unlock all features with FridgeLife Premium!',
          icon: EMOJIS.SPARKLES,
        };
    }
  };

  const content = getContent();

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="text-center mb-4">
          <div className="text-6xl mb-4">{content.icon}</div>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-4 py-2 rounded-full mb-2">
            <Crown className="w-5 h-5" />
            <span className="font-semibold">Premium Feature</span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {content.title}
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          {content.description}
        </p>

        {/* Benefits */}
        <div className="bg-gradient-to-br from-primary-mint to-light-mint p-4 rounded-xl mb-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-green" />
            Premium Benefits
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-primary-green mt-0.5">{EMOJIS.CHECKMARK}</span>
              <span><strong>Unlimited items</strong> - Track as much food as you want</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-green mt-0.5">{EMOJIS.CHECKMARK}</span>
              <span><strong>Unlimited members</strong> - Share with your whole household</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-green mt-0.5">{EMOJIS.CHECKMARK}</span>
              <span><strong>Unlimited barcode scans</strong> - Scan as many products as you need</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-green mt-0.5">{EMOJIS.CHECKMARK}</span>
              <span><strong>Advanced analytics</strong> - Detailed insights on savings & waste</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-green mt-0.5">{EMOJIS.CHECKMARK}</span>
              <span><strong>Priority support</strong> - Get help when you need it</span>
            </li>
          </ul>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-primary-green to-emerald-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-2"
          >
            <Crown className="w-5 h-5" />
            Upgrade to Premium - Only $4/month
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}
